import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/shared/hooks/useToast'
import { onRateLimited, type RateLimitedEvent } from '@/shared/lib/rateLimit'

type Cooldown = RateLimitedEvent & { until: number }

function isInlineAuthPath(path: string): boolean {
  return path.includes('/auth/') || path.includes('/sanctum/')
}

export function RateLimitNotifications() {
  const { t } = useTranslation()
  const toast = useToast()
  const [cooldown, setCooldown] = useState<Cooldown | null>(null)

  useEffect(() => onRateLimited((event) => {
    if (isInlineAuthPath(event.path)) {
      return
    }
    const seconds = Math.max(1, event.retryAfterSeconds ?? 30)
    setCooldown({ ...event, until: Date.now() + seconds * 1000 })
  }), [])

  useEffect(() => {
    if (cooldown === null) {
      return
    }

    const update = (): void => {
      const remaining = Math.max(0, Math.ceil((cooldown.until - Date.now()) / 1000))
      if (remaining === 0) {
        toast.dismissToast('rate-limit')
        setCooldown(null)
        return
      }

      toast.showToast({
        id: 'rate-limit',
        kind: 'info',
        title: t('ui.rate_limit.title'),
        description: `${t(remaining === 1 ? 'ui.rate_limit.retry_in_one' : 'ui.rate_limit.retry_in_other', { count: remaining })} ${t('ui.rate_limit.auto_resume')}`,
      })
    }

    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [cooldown, t, toast])

  return null
}
